// Rounds: a headline at the minute it broke, the market's minute candles from before it to an
// hour after, and where it happened. Pure: the page plays them and the edge scores them with the
// same code, so a score can be recomputed from the decisions alone.

import { bufferOf, bustPrice, isBust, pnlCents, toFixed } from "./money.ts";

export type Place = { lat: number; lon: number; name: string };

/** One minute: open, high, low, close, as the venue quoted them. */
export type Minute = [o: string, h: string, l: string, c: string];

export type Round = {
  id: string;
  /** What the player trades: "BTC", "ETH", "S&P 500". */
  symbol: string;
  /** The Hyperliquid coin, for a link to trade it live. */
  coin: string | null;
  headline: string;
  /** The outlet, for fresh rounds; the classics are the record. */
  source: string | null;
  /** When the headline broke, ms. */
  at: number;
  place: Place | null;
  category: string;
  reveal: string | null;
  /** The first minute's open time, ms. */
  start: number;
  /** The minute the headline broke in: the entry is its open. */
  headlineBar: number;
  bars: Minute[];
  kind: "classic" | "fresh";
  /** The first paper this round can appear in: rounds added later join the rotation the next
   * day, so a paper already played never changes under its players. */
  from?: number | undefined;
};

/** Minutes of the replay after the headline, and of context shown before it. */
export const REPLAY_MINUTES = 60;
export const CONTEXT_MINUTES = 60;
export const DECK_SIZE = 5;

/**
 * The price path a minute walks, as four points: open, the nearer extreme, the other, close. A
 * falling minute is taken to touch its high first; a rising one its low. Which came first inside
 * a minute is not recorded; this is the usual guess, and the bust is checked against both.
 */
export function pathOf(bars: Minute[], from: number, to: number): bigint[] {
  const points: bigint[] = [];
  for (let i = from; i < to && i < bars.length; i++) {
    const [o, h, l, c] = bars[i] as Minute;
    const open = toFixed(o);
    const close = toFixed(c);
    const high = toFixed(h);
    const low = toFixed(l);
    if (close < open) points.push(open, high, low, close);
    else points.push(open, low, high, close);
  }
  return points;
}

export const POINTS_PER_MINUTE = 4;

// ---------------------------------------------------------------- today's paper

/** The first paper. */
const FIRST_DAY = Date.UTC(2026, 9, 15) / 86_400_000;

/** Today's paper's number, the same everywhere: days since the first, by UTC date. */
export function dayNumber(now = Date.now()): number {
  return Math.floor(now / 86_400_000) - FIRST_DAY + 1;
}

/** mulberry32: a small seeded generator, so every reader gets the same paper. */
function seeded(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/** Five classics for the day: shuffled by the day's number, no two from the same category while
 * others are left, then in the order they happened. */
export function dailyDeck(rounds: Round[], day: number): Round[] {
  const random = seeded(day * 2_654_435_761);
  const shuffled = rounds.filter((round) => (round.from ?? 1) <= day).sort((a, b) => a.id.localeCompare(b.id));
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j] as Round, shuffled[i] as Round];
  }
  const deck: Round[] = [];
  const used = new Set<string>();
  for (const round of shuffled) {
    if (deck.length === DECK_SIZE) break;
    if (used.has(round.category)) continue;
    used.add(round.category);
    deck.push(round);
  }
  for (const round of shuffled) {
    if (deck.length === DECK_SIZE) break;
    if (!deck.includes(round)) deck.push(round);
  }
  return deck.sort((a, b) => a.at - b.at);
}

// ---------------------------------------------------------------- settling a round

export type Side = "long" | "short";

/** What the player did: a side (or none), the leverage, and the path point they closed at (null
 * for holding to the end of the hour). */
export type Decision = { side: Side | null; leverage: number; closedAt: number | null };

export type Settled = {
  pnl: bigint;
  busted: boolean;
  /** The path point the position ended at; null when sat out. */
  exitAt: number | null;
};

export const LEVERAGES = [10, 50, 200, 1000] as const;

/** Today's paper: $10,000 to start, $1,000 a headline. */
export const START_CENTS = 1_000_000n;
export const MARGIN_CENTS = 100_000n;

/** The replay's path: four points a minute from the headline's minute for the hour. */
export const replayPath = (round: Pick<Round, "bars" | "headlineBar">) => pathOf(round.bars, round.headlineBar, round.headlineBar + REPLAY_MINUTES);

/**
 * The round's result from the decision alone, exactly as papertrade would settle it: the bust is
 * checked at every path point up to the close; a close after the end of the hour is the end.
 */
export function settle(round: Pick<Round, "bars" | "headlineBar" | "symbol">, decision: Decision, margin: bigint): Settled {
  if (!decision.side) return { pnl: 0n, busted: false, exitAt: null };
  const path = replayPath(round);
  const entry = path[0];
  if (entry === undefined) return { pnl: 0n, busted: false, exitAt: null };
  const long = decision.side === "long";
  const bust = bustPrice(entry, decision.leverage, long, bufferOf(round.symbol));
  const last = decision.closedAt === null ? path.length - 1 : Math.max(0, Math.min(path.length - 1, Math.floor(decision.closedAt)));
  for (let i = 1; i <= last; i++) {
    if (isBust(path[i] as bigint, bust, long)) return { pnl: -margin, busted: true, exitAt: i };
  }
  return { pnl: pnlCents(margin, decision.leverage, long, entry, path[last] as bigint), busted: false, exitAt: last };
}

/** The best trade the hour held at this leverage: either side, closed at the best point before a
 * bust. Hindsight, for scoring the read, not a strategy. */
export function bestOf(round: Pick<Round, "bars" | "headlineBar" | "symbol">, leverage: number, margin: bigint): { side: Side; pnl: bigint; exitAt: number } {
  const path = replayPath(round);
  const entry = path[0] as bigint;
  let best: { side: Side; pnl: bigint; exitAt: number } = { side: "long", pnl: 0n, exitAt: 0 };
  for (const side of ["long", "short"] as const) {
    const long = side === "long";
    const bust = bustPrice(entry, leverage, long, bufferOf(round.symbol));
    for (let i = 1; i < path.length; i++) {
      const price = path[i] as bigint;
      if (isBust(price, bust, long)) break;
      const pnl = pnlCents(margin, leverage, long, entry, price);
      if (pnl > best.pnl) best = { side, pnl, exitAt: i };
    }
  }
  return best;
}

/** Which way the hour went, from the headline's open to the last close. */
export function hourOf(round: Pick<Round, "bars" | "headlineBar">): { from: bigint; to: bigint } {
  const path = replayPath(round);
  return { from: path[0] as bigint, to: path[path.length - 1] as bigint };
}
